// /data-export — GDPR Data Export (G3.9)
//
// Select data categories, request an async CSV/ZIP export, track status with
// 5s polling, download via pre-signed S3 URL. OWNER (50)+ only. Rate-limited
// to 1 export per tenant per 24 hours. Matches backend DataExportController
// at services/api/src/backup/data-export.controller.ts.

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  PageContainer,
  Button,
  Badge,
  Alert,
  ConfirmDialog,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  StatCard,
  DataTable,
  Pagination,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { dataExportService } from '@/services/data-export.service';
import type {
  DataExport,
  DataExportCategory,
  DataExportStatus,
} from '@/services/data-export.service';
import { ApiError } from '@/lib/api-client';
import { GLASS, cn } from '@/constants/design-tokens';

// ─── Constants ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;
const POLL_INTERVAL_MS = 5000;

type BadgeVariant = 'success' | 'info' | 'danger' | 'draft' | 'warning' | 'pending';

const STATUS_BADGE: Record<DataExportStatus, { label: string; variant: BadgeVariant }> = {
  COMPLETED: { label: 'Ready', variant: 'success' },
  IN_PROGRESS: { label: 'In Progress', variant: 'info' },
  FAILED: { label: 'Failed', variant: 'danger' },
  QUEUED: { label: 'Queued', variant: 'draft' },
};

// Category definitions — visible label, short description, table count hint, and
// a per-category accent so the selector reads as structured, not uniform.
interface CategoryMeta {
  key: DataExportCategory;
  label: string;
  description: string;
  tableCount: number;
  tone: 'indigo' | 'violet' | 'teal' | 'emerald' | 'amber' | 'rose' | 'sky' | 'fuchsia';
}

const CATEGORIES: readonly CategoryMeta[] = [
  { key: 'BILLING',            label: 'Billing & Payments',  description: 'Bills, items, payment captures',     tableCount: 3,  tone: 'indigo' },
  { key: 'PRODUCTS',           label: 'Products & Catalog',  description: 'Products, categories, combos, modifiers', tableCount: 8, tone: 'teal' },
  { key: 'INVENTORY',          label: 'Inventory & Stock',   description: 'Adjustments, transfers, wastage',    tableCount: 6,  tone: 'emerald' },
  { key: 'ORDERS_RESTAURANT',  label: 'Orders & Restaurant', description: 'Orders, KOTs, tables, recipes, menus', tableCount: 16, tone: 'violet' },
  { key: 'CRM',                label: 'CRM & Customers',     description: 'Customer records and feedback',      tableCount: 2,  tone: 'rose' },
  { key: 'LOYALTY',            label: 'Loyalty & Vouchers',  description: 'Points, memberships, vouchers',      tableCount: 6,  tone: 'fuchsia' },
  { key: 'CREDITS',            label: 'Credits',             description: 'Credit accounts, invoices, payments',tableCount: 3,  tone: 'amber' },
  { key: 'FINANCE_EXPENSES',   label: 'Finance & Expenses',  description: 'Expenses, cash closings, FX rates',  tableCount: 3,  tone: 'amber' },
  { key: 'STAFF_SHIFTS',       label: 'Staff & Shifts',      description: 'Shifts, attendance, commissions',    tableCount: 4,  tone: 'sky' },
  { key: 'LOCATIONS_USERS',    label: 'Locations & Users',   description: 'Outlets, users, floor layouts',      tableCount: 4,  tone: 'indigo' },
  { key: 'APPROVALS_AUDIT',    label: 'Approvals & Audit',   description: 'Workflows, audit logs, report jobs', tableCount: 4,  tone: 'violet' },
  { key: 'COMMUNICATIONS',     label: 'Communications',      description: 'Logs, templates, campaigns, deals',  tableCount: 7,  tone: 'sky' },
  { key: 'NOTIFICATIONS',      label: 'Notifications',       description: 'Sent notifications & preferences',   tableCount: 2,  tone: 'teal' },
  { key: 'QUOTATIONS_PO',      label: 'Quotations & POs',    description: 'Sales quotes and purchase orders',   tableCount: 4,  tone: 'emerald' },
  { key: 'FESTIVALS_BOOKINGS', label: 'Festivals & Bookings',description: 'Festive orders and booking tokens',  tableCount: 8,  tone: 'rose' },
  { key: 'CORPORATE',          label: 'Corporate Accounts',  description: 'B2B accounts and orders',            tableCount: 3,  tone: 'fuchsia' },
  { key: 'TERMINALS',          label: 'Payment Terminals',   description: 'POS terminals & push events',        tableCount: 2,  tone: 'sky' },
  { key: 'SUBSCRIPTIONS',      label: 'Subscriptions',       description: 'Plan, invoices, plan changes',       tableCount: 3,  tone: 'amber' },
] as const;

const TONE_SELECTED: Record<CategoryMeta['tone'], string> = {
  indigo:  'ring-indigo-500/70 bg-indigo-50 dark:bg-indigo-500/15',
  violet:  'ring-violet-500/70 bg-violet-50 dark:bg-violet-500/15',
  teal:    'ring-teal-500/70 bg-teal-50 dark:bg-teal-500/15',
  emerald: 'ring-emerald-500/70 bg-emerald-50 dark:bg-emerald-500/15',
  amber:   'ring-amber-500/70 bg-amber-50 dark:bg-amber-500/15',
  rose:    'ring-rose-500/70 bg-rose-50 dark:bg-rose-500/15',
  sky:     'ring-sky-500/70 bg-sky-50 dark:bg-sky-500/15',
  fuchsia: 'ring-fuchsia-500/70 bg-fuchsia-50 dark:bg-fuchsia-500/15',
};

const TONE_ICON: Record<CategoryMeta['tone'], string> = {
  indigo:  'text-indigo-600 dark:text-indigo-300',
  violet:  'text-violet-600 dark:text-violet-300',
  teal:    'text-teal-600 dark:text-teal-300',
  emerald: 'text-emerald-600 dark:text-emerald-300',
  amber:   'text-amber-600 dark:text-amber-300',
  rose:    'text-rose-600 dark:text-rose-300',
  sky:     'text-sky-600 dark:text-sky-300',
  fuchsia: 'text-fuchsia-600 dark:text-fuchsia-300',
};

// ─── Icons ─────────────────────────────────────────────────────────────────

function DownloadCloudIcon({ className = 'h-7 w-7' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 16.2A4.5 4.5 0 0017.5 8H17a6 6 0 10-11.4 3" /><polyline points="8 17 12 21 16 17" /><line x1="12" y1="12" x2="12" y2="21" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
    </svg>
  );
}

function ClockIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function ArchiveIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="4" width="20" height="5" rx="1" /><path d="M4 9v10a2 2 0 002 2h12a2 2 0 002-2V9" /><line x1="10" y1="13" x2="14" y2="13" />
    </svg>
  );
}

function ShieldIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2L4 6v6c0 5 3.5 9.5 8 10 4.5-.5 8-5 8-10V6l-8-4z" />
    </svg>
  );
}

function CalendarIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function CategoryIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function EmptyIcon() {
  return (
    <svg className="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

function formatBytes(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) { size /= 1024; i++; }
  return `${size.toFixed(1)} ${units[i]}`;
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return '00:00';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

// ─── Component ──────────────────────────────────────────────────────────────

function DataExportContent() {
  const { toast } = useToast();

  const [exports, setExports] = useState<DataExport[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  // Tenant-wide aggregates from the server (whole history, not the current
  // page) — back the "Storage Used" and "Last Export" KPI cards so they stay
  // stable across pagination.
  const [totalSizeBytes, setTotalSizeBytes] = useState(0);
  const [lastCompletedAt, setLastCompletedAt] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState<Set<DataExportCategory>>(
    () => new Set(CATEGORIES.map((c) => c.key)),
  );
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const clockRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => { document.title = 'Data Export — Orio360 Admin'; }, []);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await dataExportService.list({ page });
      setExports(result.data);
      setTotalItems(result.meta.total);
      setTotalSizeBytes(result.meta.total_size_bytes ?? 0);
      setLastCompletedAt(result.meta.last_completed_at ?? null);
      // Derive the rate-limit countdown from the server's real remaining window
      // (Redis TTL) so it's accurate and survives a page refresh, rather than
      // fabricating a fixed 24h on a 429 (issue #268).
      const remainingSec = result.meta.rate_limit_remaining_seconds ?? 0;
      setRateLimitedUntil(remainingSec > 0 ? Date.now() + remainingSec * 1000 : null);
    } catch (err) {
      toast({
        title: err instanceof ApiError ? err.message : 'Failed to load export history',
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [page, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 5s polling while any export is queued or running.
  const hasPending = exports.some((e) => e.status === 'QUEUED' || e.status === 'IN_PROGRESS');
  useEffect(() => {
    if (hasPending) {
      pollRef.current = setInterval(() => { fetchData(); }, POLL_INTERVAL_MS);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [hasPending, fetchData]);

  // 1s clock tick — only while a countdown is active, so we don't re-render
  // the entire table every second during normal use.
  useEffect(() => {
    if (rateLimitedUntil && rateLimitedUntil > Date.now()) {
      clockRef.current = setInterval(() => setNow(Date.now()), 1000);
    } else if (clockRef.current) {
      clearInterval(clockRef.current);
      clockRef.current = null;
    }
    return () => { if (clockRef.current) clearInterval(clockRef.current); };
  }, [rateLimitedUntil]);

  const toggleCategory = useCallback((key: DataExportCategory) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelected(new Set(CATEGORIES.map((c) => c.key)));
  }, []);

  const clearAll = useCallback(() => {
    setSelected(new Set());
  }, []);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const allSelected = selected.size === CATEGORIES.length;
      const payload = allSelected || selected.size === 0
        ? {}
        : { categories: [...selected] };
      await dataExportService.request(payload);
      toast({
        title: 'Data export queued — you\'ll be notified when it\'s ready.',
        variant: 'success',
      });
      await fetchData();
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 429) {
        // Rate-limited (or an export already in progress). Refresh from the
        // server so the countdown/banner reflects the real remaining window
        // rather than a fabricated 24h guess (issue #268).
        await fetchData();
        toast({
          title: err.message || 'Export limit reached — please try again later.',
          variant: 'error',
        });
      } else {
        toast({
          title: err instanceof ApiError ? err.message : 'Failed to queue export',
          variant: 'error',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [selected, fetchData, toast]);

  const handleDownload = useCallback(async (id: string) => {
    try {
      const detail = await dataExportService.getById(id);
      if (!detail.download_url) {
        toast({ title: 'Download link not available yet', variant: 'warning' });
        return;
      }
      // Completed exports stay "downloadable" for up to 120 days, but the
      // presigned S3 URL is short-lived — so an old export's link may already be
      // expired, or its underlying object aged out. Probe the URL before handing
      // the user off to a new tab; otherwise a dead link dumps them on a raw S3
      // XML error page (ExpiredToken / NoSuchKey) with no in-app feedback and no
      // way to retry (issue #269).
      let reachable = false;
      let deadReason: 'expired' | 'missing' | null = null;
      try {
        const probe = await fetch(detail.download_url, { method: 'GET' });
        reachable = probe.ok;
        if (!probe.ok) {
          deadReason = probe.status === 404 ? 'missing' : 'expired';
        }
        // We only needed the status line, not the body — don't buffer the file.
        probe.body?.cancel().catch(() => undefined);
      } catch {
        // Opaque / CORS-blocked response: we can't read the status, so we can't
        // prove the link is dead. Don't block a possibly-valid download — fall
        // through and open it as before.
        reachable = true;
      }
      if (reachable) {
        window.open(detail.download_url, '_blank', 'noopener,noreferrer');
      } else if (deadReason === 'missing') {
        toast({ title: 'Export file no longer available', variant: 'error' });
      } else {
        toast({ title: 'Download link expired — please retry', variant: 'error' });
      }
    } catch (err) {
      toast({
        title: err instanceof ApiError ? err.message : 'Failed to get download link',
        variant: 'error',
      });
    }
  }, [toast]);

  // Computed
  const pendingExport = useMemo(
    () => exports.find((e) => e.status === 'QUEUED' || e.status === 'IN_PROGRESS') ?? null,
    [exports],
  );
  const rateLimitActive = rateLimitedUntil !== null && rateLimitedUntil > now;
  const rateLimitRemaining = rateLimitActive ? rateLimitedUntil! - now : 0;
  const submitDisabled = isSubmitting || rateLimitActive || pendingExport !== null || selected.size === 0;
  const allSelected = selected.size === CATEGORIES.length;

  const columns: ColumnDef<DataExport>[] = useMemo(() => [
    {
      key: 'created_at', header: 'Requested',
      render: (_v, row) => (
        <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
          {formatTimestamp(row.completed_at ?? row.created_at)}
        </span>
      ),
    },
    {
      key: 'export_categories', header: 'Scope',
      render: (_v, row) => {
        if (!row.export_categories || row.export_categories.length === 0) {
          return <Badge variant="info" size="sm">Full export</Badge>;
        }
        const shown = row.export_categories.slice(0, 2);
        const rest = row.export_categories.length - shown.length;
        return (
          <span className="flex flex-wrap items-center gap-1">
            {shown.map((c) => {
              const meta = CATEGORIES.find((m) => m.key === c);
              return (
                <Badge key={c} variant="draft" size="sm">
                  {meta?.label ?? c}
                </Badge>
              );
            })}
            {rest > 0 && (
              <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
                +{rest}
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'file_size_bytes', header: 'Size', numeric: true,
      render: (_v, row) => (
        <span className="text-sm font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
          {formatBytes(row.file_size_bytes)}
        </span>
      ),
    },
    {
      key: 'status', header: 'Status',
      render: (_v, row) => {
        const s = STATUS_BADGE[row.status];
        return (
          <Badge variant={s.variant} size="sm" className={row.status === 'IN_PROGRESS' ? 'animate-pulse' : ''}>
            {s.label}
          </Badge>
        );
      },
    },
    {
      key: 'id', header: '',
      render: (_v, row) => (
        row.status === 'COMPLETED' ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleDownload(row.id); }}
            aria-label={`Download export from ${formatTimestamp(row.completed_at ?? row.created_at)}`}
            className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 transition-colors duration-200"
          >
            <DownloadIcon /> Download
          </button>
        ) : row.status === 'FAILED' ? (
          <span className="text-xs text-danger-500 dark:text-danger-400" title="Export failed">
            Failed
          </span>
        ) : null
      ),
    },
  ], [handleDownload]);

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  return (
    <PageContainer
      title="Data Export"
      subtitle="GDPR-compliant data portability"
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Data Export' }]}
    >
      <div className="animate-fade-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(99,102,241,0.55)] mb-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/15 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
          <div className="relative flex items-start gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/15 backdrop-blur">
              <DownloadCloudIcon className="h-7 w-7 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-bold">Data Export</h1>
                <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider backdrop-blur">
                  <ShieldIcon className="h-3.5 w-3.5" /> GDPR
                </span>
              </div>
              <p className="text-indigo-50/90">
                Select categories below, then download a ZIP of your tenant data as CSV —
                portable, compliant, and yours.
              </p>
            </div>
          </div>
        </div>

        {/* Rate-limit banner */}
        {!pendingExport && rateLimitActive && (
          <div className="mb-6 animate-slide-up">
            <Alert
              variant="warning"
              title="Daily export limit reached"
              description={`Limited to 1 export per 24 hours per tenant. Next export available in ${formatCountdown(rateLimitRemaining)}.`}
            />
          </div>
        )}

        {/* In-progress banner */}
        {pendingExport && (
          <div className="mb-6 animate-slide-up">
            <Alert
              variant="info"
              title={pendingExport.status === 'QUEUED' ? 'Export queued' : 'Export in progress'}
              description="We're preparing your data now. This page refreshes automatically — usually 1–3 minutes."
            />
          </div>
        )}

        {/* Stat Cards */}
        {isLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map((i) => <StatCard key={i} title="" value="" loading />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="animate-slide-up">
              <StatCard title="Last Export" value={formatTimestamp(lastCompletedAt)} icon={<ClockIcon />} />
            </div>
            <div className="animate-slide-up" style={{ animationDelay: '50ms' }}>
              <StatCard title="Total Exports" value={totalItems.toLocaleString('en-IN')} icon={<ArchiveIcon />} />
            </div>
            <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
              <StatCard title="Storage Used" value={formatBytes(totalSizeBytes)} icon={<DownloadCloudIcon className="h-4 w-4" />} />
            </div>
            <div className="animate-slide-up" style={{ animationDelay: '150ms' }}>
              <StatCard title="Download Window" value="120 days" icon={<CalendarIcon />} />
            </div>
          </div>
        )}

        {/* Category selector */}
        <section className={cn(GLASS, 'p-6 mb-6 animate-fade-in')} aria-labelledby="category-heading">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300">
                <CategoryIcon className="h-5 w-5" />
              </span>
              <div>
                <h2 id="category-heading" className="text-sm font-bold text-neutral-800 dark:text-neutral-200 uppercase tracking-wider">
                  What to export
                </h2>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  <span className="font-mono tabular-nums">{selected.size}</span> of {CATEGORIES.length} categories selected
                  {allSelected && <> — <span className="text-indigo-600 dark:text-indigo-400 font-semibold">full GDPR export</span></>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <button
                type="button"
                onClick={selectAll}
                className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 font-medium transition-colors duration-200"
              >
                Select all
              </button>
              <span className="text-neutral-300 dark:text-neutral-600">•</span>
              <button
                type="button"
                onClick={clearAll}
                className="text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 font-medium transition-colors duration-200"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {CATEGORIES.map((cat) => {
              const isSelected = selected.has(cat.key);
              return (
                <button
                  key={cat.key}
                  type="button"
                  role="checkbox"
                  aria-checked={isSelected}
                  onClick={() => toggleCategory(cat.key)}
                  className={cn(
                    'group relative flex items-start gap-3 rounded-xl p-4 text-left transition-all duration-200 ring-1 ring-inset',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500',
                    isSelected
                      ? cn(TONE_SELECTED[cat.tone], 'ring-2 shadow-sm')
                      : 'ring-neutral-200/70 dark:ring-neutral-700/70 bg-white/60 dark:bg-neutral-800/40 hover:bg-white dark:hover:bg-neutral-800 hover:shadow-sm',
                  )}
                >
                  <span
                    className={cn(
                      'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200/80 dark:ring-neutral-700/80',
                      TONE_ICON[cat.tone],
                    )}
                  >
                    <CategoryIcon className="h-[18px] w-[18px]" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                        {cat.label}
                      </span>
                      <span
                        aria-hidden="true"
                        className={cn(
                          'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border transition-colors duration-150',
                          isSelected
                            ? 'bg-indigo-600 border-indigo-600 text-white dark:bg-indigo-500 dark:border-indigo-500'
                            : 'border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800',
                        )}
                      >
                        {isSelected && (
                          <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 8 7 12 13 4" />
                          </svg>
                        )}
                      </span>
                    </span>
                    <span className="mt-0.5 block text-xs text-neutral-500 dark:text-neutral-400 truncate">
                      {cat.description}
                    </span>
                    <span className="mt-1 block text-[10px] font-mono uppercase tracking-wider text-neutral-400 dark:text-neutral-500 tabular-nums">
                      {cat.tableCount} tables
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-5 border-t border-neutral-200/60 dark:border-neutral-700/60">
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Export is rate-limited to <span className="font-mono tabular-nums">1 per 24 hours</span>. Completed
              files remain downloadable for <span className="font-mono tabular-nums">120 days</span>.
            </p>
            <Button
              variant="primary"
              size="md"
              onClick={() => setConfirmOpen(true)}
              disabled={submitDisabled}
              loading={isSubmitting}
            >
              {pendingExport
                ? 'Export in progress…'
                : rateLimitActive
                ? `Available in ${formatCountdown(rateLimitRemaining)}`
                : allSelected
                ? 'Generate full export'
                : `Generate export (${selected.size})`}
            </Button>
          </div>
        </section>

        {/* History */}
        {isLoading ? (
          <div className={cn(GLASS, 'p-6 space-y-3')}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex gap-4">
                <Skeleton variant="text" width="20%" height={16} />
                <Skeleton variant="rect" width={90} height={22} />
                <Skeleton variant="text" width="10%" height={16} />
                <Skeleton variant="rect" width={70} height={22} />
                <Skeleton variant="text" width="10%" height={16} />
              </div>
            ))}
          </div>
        ) : exports.length === 0 ? (
          <div className={cn(GLASS, 'py-4')}>
            <EmptyState
              variant="page"
              icon={<EmptyIcon />}
              title="No exports yet"
              description="Pick the categories you need above, then generate your first data export."
            />
          </div>
        ) : (
          <>
            <div className={cn(GLASS, 'overflow-hidden mb-6')}>
              <div className="px-5 py-4 flex items-center justify-between">
                <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                  Export History ({totalItems.toLocaleString('en-IN')})
                </h3>
                {hasPending && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-indigo-600 dark:text-indigo-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-indigo-500 animate-pulse" />
                    Live — refreshing every 5s
                  </span>
                )}
              </div>
              <DataTable<DataExport> columns={columns} data={exports} />
            </div>
            {totalPages > 1 && (
              <div className="mb-6">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={PAGE_SIZE}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Generate data export"
        description={
          allSelected
            ? 'Export all data categories as a single CSV/ZIP bundle. You\'ll receive a notification when ready (usually 1–3 minutes). Limited to 1 export per 24 hours.'
            : `Export ${selected.size} categor${selected.size === 1 ? 'y' : 'ies'} as CSV/ZIP. You can download the bundle for 120 days after it's ready. Limited to 1 export per 24 hours.`
        }
        confirmLabel="Generate export"
        variant="warning"
        onConfirm={() => {
          setConfirmOpen(false);
          void handleSubmit();
        }}
        onCancel={() => setConfirmOpen(false)}
        loading={isSubmitting}
      />
    </PageContainer>
  );
}

export function DataExportPage() {
  return (
    <ErrorBoundary>
      <DataExportContent />
    </ErrorBoundary>
  );
}
